/**
 * Kanban utility types and display functions for the frontend.
 * Server-side-only logic (transition validation, parent derivation, validation status
 * derivation, children validation) has been removed in US-057. Those functions now
 * live exclusively in the backend container.
 *
 * Per FS-005.10.3, tickets carry a direct `column: KanbanColumn` field and top-level
 * workflow data (`validationFeedback`, `reviewComments`, `reviewLabels`, `testResults`).
 * The legacy `ColumnState` tagged union and its `getColumnFromState` helper were
 * removed in that refactor.
 *
 * @implements FS-005 - Roadmap Management
 * @implements FS-005.2.1 - Kanban Column Structure
 * @implements FS-005.10.3 - Direct column field (no tagged union wrapper)
 * @implements US-057 - Client MobX Store + Subscription Wiring
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * KanbanColumn enum representing the 7 kanban columns.
 * Columns are ordered from backlog (0) to done (6).
 * New tickets always start in backlog.
 *
 * @implements FS-005.2.1 - Kanban Column Structure
 * @implements BS-005.16 - Column Transitions
 */
export enum KanbanColumn {
  BACKLOG = 'backlog',
  CONSOLIDATION = 'consolidation',
  TODO = 'todo',
  IN_PROGRESS = 'inProgress',
  REVIEW = 'review',
  QA = 'qa',
  DONE = 'done',
}

/**
 * Ticket lifecycle state enum.
 * Independent of kanban column - controls what operations are allowed.
 *
 * @implements FS-005 Data Requirements - Ticket Lifecycle States
 * @implements BS-005.15 - Lifecycle State Restricts Column Changes
 */
export enum LifecycleState {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  ARCHIVED = 'archived',
}

/**
 * Validation status type. DERIVED from validationFeedback[], never stored.
 * Computed by the server-side container.
 *
 * @implements FS-005.8.3 - Representation (Derived Values)
 * @implements BS-005.14 - Validation Status Required for Todo
 */
export type ValidationStatus = 'pending' | 'validated' | 'issues';

// ============================================================================
// Interfaces for column-specific data
// ============================================================================

/**
 * Agent type for validation feedback during consolidation.
 * @implements FS-005.8.4 - NAP Reactions
 */
export type ValidationAgentType = 'BACKEND_DEV' | 'FRONTEND_DEV' | 'QA';

/**
 * Validation feedback status.
 * @implements FS-005.2.2 - Consolidation Validation Phase
 */
export type ValidationFeedbackStatus = 'APPROVED' | 'ISSUES';

/**
 * Validation feedback from developer and QA agents during consolidation.
 *
 * @implements FS-005.2.2 - Consolidation Validation Phase
 * @implements FS-005.8.4 - NAP Reactions (QA agent added)
 */
export interface ValidationFeedback {
  /** The type of agent providing feedback */
  agentType: ValidationAgentType;
  /** Whether the agent approved or found issues */
  status: ValidationFeedbackStatus;
  /** Optional detailed feedback text */
  feedback?: string;
  /** ISO 8601 timestamp of when feedback was provided */
  timestamp: string;
}

/**
 * Review comment status.
 * @implements FS-005.2.1 - review column
 */
export type ReviewCommentStatus = 'APPROVED' | 'CHANGES_REQUESTED';

/**
 * Review comment from code review process.
 *
 * @implements FS-005.2.1 - Kanban Column Structure (review column)
 */
export interface ReviewComment {
  reviewer: string;
  status: ReviewCommentStatus;
  comment?: string;
  timestamp: string;
}

/**
 * Test type for QA column test results.
 * @implements FS-005.2.1 - QA Column Details
 */
export type TestType = 'UNIT' | 'INTEGRATION' | 'E2E' | 'BUSINESS';

/**
 * Test result status.
 * @implements FS-005.2.1 - QA Column Details
 */
export type TestResultStatus = 'PASSED' | 'FAILED';

/**
 * Test result from QA testing.
 *
 * @implements FS-005.2.1 - Kanban Column Structure (QA column)
 */
export interface TestResult {
  testType: TestType;
  status: TestResultStatus;
  details?: string;
  timestamp: string;
}

// ============================================================================
// Column Order and Display Utilities
// ============================================================================

/**
 * Ordered array of kanban columns from earliest to latest.
 * Used for column index lookups and display ordering.
 *
 * @implements BS-005.16 - Column Transitions
 * @implements BS-005.17 - Parent Column Is Always Derived
 */
export const COLUMN_ORDER: KanbanColumn[] = [
  KanbanColumn.BACKLOG,
  KanbanColumn.CONSOLIDATION,
  KanbanColumn.TODO,
  KanbanColumn.IN_PROGRESS,
  KanbanColumn.REVIEW,
  KanbanColumn.QA,
  KanbanColumn.DONE,
];

/**
 * Returns the ordinal index of a kanban column.
 * BACKLOG=0, CONSOLIDATION=1, TODO=2, IN_PROGRESS=3, REVIEW=4, QA=5, DONE=6.
 *
 * @param column - The kanban column
 * @returns The zero-based index in the column order
 * @implements BS-005.16 - Column Transitions
 */
export function getColumnIndex(column: KanbanColumn): number {
  return COLUMN_ORDER.indexOf(column);
}

